import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { UserRole } from '../components/actions/ActionsPanel';

// Define the context type
type PinnedActionsContextType = {
  pinnedActionIdsByRole: Record<UserRole, Set<string>>;
  togglePinAction: (role: UserRole, actionId: string) => void;
  isPinned: (role: UserRole, actionId: string) => boolean;
  getActionPinsByRole: (role: UserRole) => Set<string>;
  clearPinsForRole: (role: UserRole) => void;
};

// Storage key for localStorage
const STORAGE_KEY = 'pinnedActionsByRole';

// Helper function to convert Sets to arrays for JSON serialization
const serializePinnedActions = (pinnedActionsByRole: Record<UserRole, Set<string>>): Record<UserRole, string[]> => {
  const serialized: Record<string, string[]> = {};
  
  Object.keys(pinnedActionsByRole).forEach(role => {
    serialized[role] = Array.from(pinnedActionsByRole[role as UserRole]);
  });
  
  return serialized as Record<UserRole, string[]>;
};

// Helper function to convert arrays back to Sets after JSON deserialization
const deserializePinnedActions = (serializedActions: Record<UserRole, string[]>): Record<UserRole, Set<string>> => {
  const deserialized: Record<string, Set<string>> = {};
  
  Object.keys(serializedActions).forEach(role => {
    deserialized[role] = new Set(serializedActions[role as UserRole]);
  });
  
  return deserialized as Record<UserRole, Set<string>>;
};

// Create the context with a default value
const PinnedActionsContext = createContext<PinnedActionsContextType | undefined>(undefined);

// Provider component
export const PinnedActionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Initialize state with data from localStorage or default empty sets
  const [pinnedActionIdsByRole, setPinnedActionIdsByRole] = useState<Record<UserRole, Set<string>>>(() => {
    // Try to load from localStorage
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        return deserializePinnedActions(parsedData);
      }
    } catch (error) {
      console.error('Error loading pinned actions from localStorage:', error);
    }
    
    // Default empty state if nothing in localStorage
    return {
      'CEO': new Set<string>(),
      'VP of Lending': new Set<string>(),
      'Loan Advisor': new Set<string>()
    };
  });

  // Save to localStorage whenever the pinned actions change
  useEffect(() => {
    try {
      // Log current pinned actions
      console.log('Saving pinned actions to localStorage:', pinnedActionIdsByRole);
      
      const serialized = serializePinnedActions(pinnedActionIdsByRole);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(serialized));
      
      // Verify storage worked by reading it back
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        console.log('Verified localStorage save:', storedData);
      }
    } catch (error) {
      console.error('Error saving pinned actions to localStorage:', error);
    }
  }, [pinnedActionIdsByRole]);

  // Toggle pin state for an action
  const togglePinAction = (role: UserRole, actionId: string) => {
    setPinnedActionIdsByRole(prev => {
      const updated = { ...prev };
      const newPinnedIds = new Set(prev[role]);
      
      if (newPinnedIds.has(actionId)) {
        // If already pinned, unpin it
        newPinnedIds.delete(actionId);
        console.log(`Unpinned action: ${actionId} from ${role} view`);
      } else {
        // If not pinned, pin it
        newPinnedIds.add(actionId);
        console.log(`Pinned action: ${actionId} to ${role} view`);
      }
      
      updated[role] = newPinnedIds;
      return updated;
    });
  };

  // Check if an action is pinned
  const isPinned = (role: UserRole, actionId: string) => {
    return pinnedActionIdsByRole[role].has(actionId);
  };

  // Get all pinned action IDs for a role
  const getActionPinsByRole = (role: UserRole) => {
    return pinnedActionIdsByRole[role];
  };

  // Clear all pins for a role
  const clearPinsForRole = (role: UserRole) => {
    setPinnedActionIdsByRole(prev => {
      const updated = { ...prev };
      updated[role] = new Set<string>();
      return updated;
    });
  };

  // Value to be provided to consumers
  const contextValue: PinnedActionsContextType = {
    pinnedActionIdsByRole,
    togglePinAction,
    isPinned,
    getActionPinsByRole,
    clearPinsForRole
  };

  return (
    <PinnedActionsContext.Provider value={contextValue}>
      {children}
    </PinnedActionsContext.Provider>
  );
};

// Custom hook to use the context
export const usePinnedActions = () => {
  const context = useContext(PinnedActionsContext);
  if (context === undefined) {
    throw new Error('usePinnedActions must be used within a PinnedActionsProvider');
  }
  return context;
}; 